import { effect, inject, Injectable, signal } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { ChatTokenResponse, ListTokenResponse } from '../interfaces/socket-tokens';
import { ChatMessage, ChatMessagesResponse } from '../interfaces/chat-message';

import { EchoManager, EchoChannelConfig } from './echo-manager';
import { jsonToFormData } from '../helpers/jsonToFormData';
import { downloadBlob } from '../helpers/downloadBlob';
import { Note } from '../interfaces/note';
import { tap } from 'rxjs';
import { ClientsResponse } from '../models/client.model';
import { UserService } from './user.service';
import { RefreshersService } from './refreshers.service';


@Injectable({
  providedIn: 'root',
})
export class TicketChatService {

  baseUrl = environment.apiUrl;
  chatUrl = environment.chatUrl;
  chatApiUrl = this.chatUrl + 'api/conversations/';

  http = inject(HttpClient);
  router = inject(Router);
  user = inject(UserService);
  refreshers = inject(RefreshersService);

  // tieckts list
  ticketsListToken = signal<ListTokenResponse | null>(null)
  ticketsListEchoManager: EchoManager | null = null

  // notes
  notesList = signal<Note[]>([])
  noteAction = signal<'init' | 'created' | 'updated' | 'deleted'>('init')


  // chat
  chatAccess = signal<ChatTokenResponse | null>(null)
  messages = signal<ChatMessagesResponse | null>(null)
  applyScroll = signal<boolean>(false)
  typing = signal(false)
  chatEchoManager: EchoManager | null = null
  private typingTimeout: any = null

  updatedTicket = signal<any | null>(null)

  constructor() {
    effect(() => {
      if (this.applyScroll()) {
        setTimeout(() => this.applyScroll.set(false), 1);
      }
    },)
  }
  getChatToken(body: any, openSocket: boolean) {
    this.clearChat()
    if (body.ticket_id) {
      this.http.post<ChatTokenResponse>(`${this.baseUrl}chat-token`, body).subscribe({
        next: (response) => {
          this.chatAccess.set(response);
          this.getTicketMessages(body.ticket_id);
          if (openSocket) {
            this.initChatEcho(response, body.ticket_id);
          }
        }
      });
    }
  }
  getTicketsListToken() {
    this.ticketsListToken.set(null)
    this.ticketsListEchoManager?.disconnect();
    this.ticketsListEchoManager = null;
    this.http.post<ListTokenResponse>(`${this.baseUrl}list-token`, {}).subscribe({
      next: (response) => {
        this.ticketsListToken.set(response)
        this.initTicketsListEcho(response);
      }
    });
  }
  getTicketMessages(ticketId: number) {
    this.http.get<ChatMessagesResponse>(`${this.chatApiUrl}${ticketId}/messages`).subscribe({
      next: (response) => {
        this.messages.set(response);
        this.applyScroll.set(true)
      }
    });
  }
  getTicketNotes(ticketId: number, params: any = {}) {
    this.http.get<Note[]>(`${this.baseUrl}tickets/${ticketId}/notes`, { params }).subscribe({
      next: (res) => {
        this.notesList.set(res)
      }
    });
  }

  sendMessage(ticketId: number, msgBody: any) {
    const formData = jsonToFormData(msgBody);
    return this.http.post(`${this.chatApiUrl}${ticketId}/messages`, formData)
  }
  editMessage(ticketId: number, messageId: number, body: any) {
    return this.http.put(`${this.chatApiUrl}${ticketId}/messages/${messageId}`, body)
  }
  deleteMessage(ticketId: number, messageId: number) {
    return this.http.delete(`${this.chatApiUrl}${ticketId}/messages/${messageId}`)
  }

  downloadAttachment(id: number, filename: string = 'attachment') {
    return this.http.get(`${this.chatUrl}api/attachments/${id}/download`, { responseType: 'blob' }).pipe(
      tap(blob => {
        downloadBlob(blob, filename);
      })
    )
  }

  clearChat() {
    this.chatAccess.set(null);
    this.messages.set(null);
    this.typing.set(false);
    this.chatEchoManager?.disconnect();
    this.chatEchoManager = null;
    this.notesList.set([])
    this.noteAction.set('init')
  }


  private initChatEcho(response: ChatTokenResponse, ticketId: number) {
    this.chatEchoManager = new EchoManager('Chat');
    this.chatEchoManager.init(this.createEchoConfig(response.websocket, response.chat_token));
    this.chatEchoManager.setupConnectionStatus({
      onBackOnline: () => {
        this.getTicketMessages(ticketId);
      }
    });

    const channelsConfig: EchoChannelConfig[] = [
      {
        type: 'join',
        channel: `ticket.${response.data_center}.${ticketId}`,
        listen: [
          {
            event: '.MessageSent',
            callback: (message: ChatMessage) => {
              this.onMessageAdded(message);
            }
          },
          {
            event: '.MessageUpdated',
            callback: (message: ChatMessage) => {
              this.onMessageUpdated(message);
            }
          },
          {
            event: '.MessageDeleted',
            callback: (message: ChatMessage) => {
              this.onMessageDeleted(message);
            }
          },
        ],
        listenForWhisper: [
          {
            event: 'ClientTyping',
            callback: (event: any) => {
              this.onClientTyping(event?.value || false);
            }
          },
        ],
      },
      {
        type: 'channel',
        channel: `ticket.${response.data_center}.${ticketId}.notes`,
        listen: [
          {
            event: '.SupportAgentNotesUpdated',
            callback: (data: any) => {
              this.onNoteEvent(data);
            }
          },
        ],
      },
    ];

    this.chatEchoManager.subscribeToChannels(channelsConfig);
  }

  private initTicketsListEcho(response: ListTokenResponse) {
    this.ticketsListEchoManager = new EchoManager('TicketsList');
    this.ticketsListEchoManager.init(this.createEchoConfig(response.websocket, response.token));
    this.ticketsListEchoManager.setupConnectionStatus();

    const channelsConfig: EchoChannelConfig[] = [
      {
        type: 'channel',
        channel: response.channel,
        listen: [
          {
            event: '.SupportAgentTicketsUpdated',
            callback: (data: any) => {
              this.updatedTicket.set(data?.ticket);
            }
          },
        ],
      },
      {
        type: 'channel',
        channel: `sidlist.${this.user.user()?.id}`,
        listen: [
          {
            event: '.TicketsUpdated',
            callback: (data: any) => {
              this.refreshers.clientsO.next(data)
            }
          },
        ],
      },
    ];

    this.ticketsListEchoManager.subscribeToChannels(channelsConfig);
  }

  // Echo event handlers
  private onMessageAdded(message: ChatMessage) {
    this.messages.update(val => val ? { ...val, messages: [...val.messages, message] } : null);
    this.applyScroll.set(true);
  }

  private onMessageUpdated(message: ChatMessage) {
    this.messages.update(val => val ? {
      ...val,
      messages: val.messages.map(m => m.id === message.id ? { ...m, ...message } : m)
    } : null);
  }

  private onMessageDeleted(message: ChatMessage) {
    this.messages.update(val => val ? {
      ...val,
      messages: val.messages.filter(m => m.id !== message.id)
    } : null);
  }

  private onClientTyping(isTyping: boolean) {
    this.typing.set(isTyping);

    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
      this.typingTimeout = null;
    }

    if (isTyping) {
      this.typingTimeout = setTimeout(() => this.typing.set(false), 10000);
    }
  }

  private onNoteEvent(data: any) {
    if (data.action === "created") {
      this.notesList.update(notes => [...notes, data.note]);
    } else if (data.action === "deleted") {
      this.notesList.update(notes => notes.filter(note => note.id !== data.note.id));
    }
    this.noteAction.set(data.action);
  }

  // Public methods
  triggerTyping(value: boolean, ticketId: string) {
    const channel = this.chatEchoManager?.getChannel(
      `ticket.${this.chatAccess()?.data_center}.${ticketId}`
    );
    channel?.whisper('AgentTyping', { value });
  }

  private createEchoConfig(websocket: any, token: string) {
    return {
      broadcaster: 'reverb',
      key: websocket.app_key,
      wsHost: websocket.client_host,
      wsPort: websocket.client_port,
      wssPort: websocket.client_port,
      forceTLS: websocket.client_use_tls,
      enabledTransports: ['ws', 'wss'],
      authEndpoint: `${this.chatUrl}broadcasting/auth`,
      auth: {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      },
    };
  }

}
